import type { UserBasketSettingsLocationFragment } from '@/generated/graphql'
import { ServiceType } from '@/generated/graphql'
import { getCountryCodeFromName } from '@/helpers/multiRegion/countries'
import { gql, useQuery } from '@apollo/client'
import { basketLocationToAddress } from '@caterdesk/utils--gm-validation'

export const GET_VENDORS_IN_RANGE_LOCATIONS = gql`
  query GetVendorsInRange(
    $vendorId: ID!
    $lat: Float!
    $lng: Float!
    $serviceType: ServiceType!
    $countryCode: String
    $dateTime: VendorListDateSearchInput
    $headCount: Int
  ) {
    vendor(id: $vendorId) {
      id
      inRangeLocations(
        lat: $lat
        lng: $lng
        serviceType: $serviceType
        countryCode: $countryCode
        dateTime: $dateTime
        headCount: $headCount
      ) {
        id
        timezone
        country
        city
        details {
          minOrderValue
          minOrderValueExTax
          gmMaxDistanceMinutes
          gmMinNoticePeriod
          gmMaxOrder
          gmLargeOrderThreshold
          gmLargeOrderNoticePeriod
          isGMActive
          gmAllowCountryWideDelivery
          effectiveMinimumNoticePeriod
        }
        closingDates {
          closedWeekdays
          closedDates
        }
        openingTimes {
          startTime
          endTime
          weekdays
        }
        deliveryRegions(input: { location: { lat: $lat, lng: $lng }, serviceType: $serviceType }) {
          id
          minOrderValue
          minOrderValueExTax
          orderPosition
          serviceType
          deliveryFee {
            total
            totalExTax
            taxRate
          }
        }
      }
    }
  }
`

type UseVendorInRangeLocationsParams = {
  vendorId?: string
  location?: UserBasketSettingsLocationFragment | null
  headCount?: number | null
  skip?: boolean
}

export type InRangeLocationData = {
  id: string
  deliveryRegions: {
    id: string
    serviceType: ServiceType
    orderPosition: number
    deliveryFee?: {
      totalExTax?: number | null
    } | null
    minOrderValueExTax?: number | null
  }[]
  details?: {
    effectiveMinimumNoticePeriod?: number | null
  }
}

type UseVendorInRangeLocationsResult = {
  inRangeLocations: InRangeLocationData[]
  loading: boolean
  error?: Error
}

/**
 * Hook to access vendor in-range locations from Apollo cache.
 * This hook will read from cache first, only fetching if data is not available.
 */
export const useVendorInRangeLocations = ({
  vendorId,
  location,
  headCount,
  skip = false,
}: UseVendorInRangeLocationsParams): UseVendorInRangeLocationsResult => {
  const { latitude, longitude, country } = location ? basketLocationToAddress(location) : {}
  const countryCode = country ? getCountryCodeFromName(country) : null

  const shouldSkip =
    skip ||
    !vendorId ||
    !latitude ||
    !longitude ||
    typeof latitude !== 'number' ||
    typeof longitude !== 'number'

  const { data, loading, error } = useQuery(GET_VENDORS_IN_RANGE_LOCATIONS, {
    variables: {
      vendorId: vendorId ?? '',
      lat: latitude ?? 0,
      lng: longitude ?? 0,
      serviceType: ServiceType.Gm,
      countryCode,
      dateTime: null,
      headCount: headCount ?? null,
    },
    skip: shouldSkip,
    fetchPolicy: 'cache-first',
  })

  return {
    inRangeLocations: data?.vendor?.inRangeLocations ?? [],
    loading,
    error,
  }
}
